import { useState } from "react";

interface ValidationError {
  id: string;
  error: string;
}

/**
 * Custom hook for recipe validation logic
 * Follows Single Responsibility Principle - handles only validation
 */
export const useRecipeValidation = (getValues: () => any) => {
  const [processTotal, setProcessTotal] = useState<number>(0);
  const [ingredientTotal, setIngredientTotal] = useState<number>(0);
  const [validationError, setValidationError] = useState<ValidationError>({
    id: "",
    error: ""
  });

  const validateTotalValue = (id: string, value: string): boolean => {
    setValidationError({ id: "", error: "" });

    if (isNaN(parseFloat(value))) {
      setValidationError({
        id,
        error: "Please enter a valid number"
      });

      const formData: any = getValues();
      for (const key in formData) {
        if (key.includes("process")) {
          setProcessTotal(0);
        } else if (key.includes("ingredient")) {
          setIngredientTotal(0);
        }
      }
      return true;
    } else {
      const formData: any = getValues();
      let totalProcessValue = 0;
      let totalIngValue = 0;

      for (const key in formData) {
        if (key.includes("process") && key.split("-")[1] && formData[key].value) {
          totalProcessValue += parseFloat(formData[key].value);
          setProcessTotal(totalProcessValue);
        } else if (key.includes("ingredient") && key.split("-")[2] && formData[key].value) {
          totalIngValue += parseFloat(formData[key].value);
          setIngredientTotal(totalIngValue);
        }
      }
      return true;
    }
  };

  const validateRecipeValuePercent = (value: any): boolean | string => {
    if (!value || value === "") return true;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "Please enter a valid number";
    if (numValue < 0) return "Value cannot be negative";
    if (numValue > 100) return "Value cannot exceed 100%";
    return true;
  };

  const isFormValid = (): boolean => {
    return !(
      processTotal > 100 ||
      ingredientTotal > 100 ||
      Number(processTotal) + Number(ingredientTotal) > 100
    );
  };

  const getErrorMessage = (): string => {
    if (processTotal > 100) {
      return "Sum of recipe process(es) value(s) should be less than or equal to 100%";
    }
    if (ingredientTotal > 100) {
      return "Sum of recipe ingredient(s) value(s) should be less than or equal to 100%";
    }
    if (Number(processTotal) + Number(ingredientTotal) > 100) {
      return "Sum of ingredient(s) value(s) and process(es) value(s) should be less than or equal to 100%";
    }
    return "";
  };

  return {
    processTotal,
    ingredientTotal,
    validationError,
    validateTotalValue,
    validateRecipeValuePercent,
    isFormValid,
    getErrorMessage
  };
};
